import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay, Navigation } from 'swiper/modules';
import { useEffect, useRef } from 'react';
import 'swiper/css';
import 'swiper/css/navigation';
import frogImg from "../../assets/footer-frog.png"
import { ArrowLeft, ArrowRight } from 'lucide-react';

const SubCategorySwiper = ({ subcategories, onSelectSubCategory, selectedSubCategory, createPage, loading }) => {
    const swiperRef = useRef(null);
    const isInitialMount = useRef(true);

    useEffect(() => {
        const swiper = swiperRef.current?.swiper;
        if (!swiper) return;

        // Always stop autoplay first
        swiper.autoplay.stop();

        // Only slide to position on initial mount or if URL changed
        if (isInitialMount.current && selectedSubCategory) {
            const selectedIndex = subcategories.findIndex(
                subcat => subcat.subcategory_id === selectedSubCategory.subcategory_id
            );

            if (selectedIndex !== -1) {
                // Disable transition temporarily
                swiper.params.speed = 0;
                // Move to the correct slide
                swiper.slideTo(selectedIndex - 1, 0);
                // Re-enable transition
                setTimeout(() => {
                    swiper.params.speed = 300;
                }, 100);
            }
        }

        // Start autoplay only if no subcategory is selected and enough slides
        if (!selectedSubCategory && subcategories?.length > 3 && !loading) {
            swiper.autoplay.start();
        }

        // Mark initial mount as complete
        isInitialMount.current = false;

        // Cleanup function
        return () => {
            if (swiper && swiper.autoplay) {
                swiper.autoplay.stop();
            }
        };
    }, [selectedSubCategory, subcategories, loading]);

    return (
        <div className={"w-full mx-auto relative" + (createPage ? " mt-0 " : " my-4 px-4 ")}>
            {!createPage && <>
                <div className="absolute left-8 top-0 bottom-0 w-16 sm:w-24 
                    bg-gradient-to-r from-slate-950 via-slate-950/50 to-transparent 
                    dark:from-pink-200/20 dark:via-pink-100/10 dark:to-transparent 
                    z-[1] pointer-events-none" 
                />
                <div className="absolute right-8 top-0 bottom-0 w-16 sm:w-24 
                    bg-gradient-to-l from-slate-950 via-slate-950/50 to-transparent 
                    dark:from-pink-200/20 dark:via-pink-100/10 dark:to-transparent 
                    z-[1] pointer-events-none" 
                />
            </>}
            
            <button disabled={loading} className={
                "swiper-button-prev z-[2] rounded-md p-0.5 after:!hidden flex items-center justify-center " +
                "!text-slate-100 dark:!text-white " +
                "!bg-slate-700 dark:!bg-pink-400 " +
                "hover:!bg-green-400 dark:hover:!bg-pink-400 " +
                "hover:!text-black dark:hover:!text-white dark:!text-white " +
                `${subcategories && subcategories.length > 3 ? "!opacity-100" : "!opacity-0"}` +
                (createPage ? " !top-[26px] !w-6 !h-6 !left-0 " : " !w-7 !h-7 !top-[29px] !left-1 ")
            }>
                <ArrowLeft className="w-4 h-4" />
            </button>

            <button disabled={loading} className={
                "swiper-button-next z-[2] rounded-md p-0.5 after:!hidden flex items-center justify-center " +
                "!text-slate-100 dark:!text-white " +
                "!bg-slate-700 dark:!bg-pink-400 " +
                "hover:!bg-green-400 dark:hover:!bg-pink-400 " +
                "hover:!text-black dark:hover:!text-pink-950 " +
                `${subcategories && subcategories.length > 3 ? "!opacity-100" : "!opacity-0"}` +
                (createPage ? " !top-[26px] !w-6 !h-6 !right-0 " : " !w-7 !h-7 !top-[29px] !right-1 ")
            }>
                <ArrowRight className="w-4 h-4" />
            </button>

            <Swiper
                ref={swiperRef}
                modules={[Autoplay, Navigation]}
                spaceBetween={10}
                slidesPerView="auto"
                loop={subcategories?.length > 3}
                autoplay={{
                    delay: createPage ? 1500 : 2000,
                    disableOnInteraction: false,
                    pauseOnMouseEnter: true,
                }}
                className="mySwiper relative z-0 mx-8"
                initialSlide={0}
                speed={300}
                navigation={{
                    prevEl: '.swiper-button-prev',
                    nextEl: '.swiper-button-next',
                    disabledClass: 'opacity-50'
                }}
            >
                {subcategories?.map((subcat) => (
                    <SwiperSlide
                        key={subcat.subcategory_id}
                        className={"!w-auto " + (createPage ? "min-w-auto" : "min-w-[100px]")}
                    >
                        <button
                            disabled={loading}
                            onClick={() => onSelectSubCategory(subcat)}
                            className={`
                                flex items-center gap-2 pl-1 pr-2.5 py-1 rounded-md transition-all w-full 
                                ${selectedSubCategory?.subcategory_id === subcat.subcategory_id
                                    ? 'bg-gradient-to-br from-green-400 via-green-400 to-teal-400 dark:from-pink-900 dark:via-pink-800 dark:to-pink-900 text-black dark:text-white'
                                    : 'hover:bg-slate-800 dark:hover:bg-pink-500/90 bg-slate-800/70 dark:bg-pink-300 text-slate-200 dark:text-slate-800'


                                }`}
                        >
                            <img
                                src={subcat.image_url ? subcat.image_url : frogImg}
                                alt={subcat.name}
                                className={
                                    "rounded object-cover bg-black dark:bg-pink-950 " + 
                                    (createPage ? "w-6 h-6" : "w-8 h-8")
                                }
                            />
                            <span className="text-xs font-medium truncate">
                                {subcat.name}
                            </span>
                        </button>
                    </SwiperSlide>
                ))}
            </Swiper>
        </div>
    );
};

export default SubCategorySwiper; 